/**
 * Buehnen-Modus: Vollbild, grosse Schrift, Songwechsel per Wischen,
 * Bildschirm bleibt an, Auto-Scroll direkt startbar.
 *
 * Fuer einen einzelnen Song (#/stage/song/:id) oder eine Setlist
 * (#/stage/setlist/:id/:index). Ein Songwechsel geht ueber den Router, damit
 * Zeichenebene und Zustand sauber neu aufgebaut werden.
 */
import type { View } from '../app.ts';
import { navigate, refreshDrawing } from '../app.ts';
import type { Song } from '../types.ts';
import {
  getSetlist,
  getSettings,
  getSong,
  saveSettings,
  saveSong,
  songSurfaceId,
} from '../db/repo.ts';
import { createSheetPanel } from '../ui/sheet-panel.ts';
import { createAudioPanel } from '../ui/audio-panel.ts';
import { keepAwake } from '../features/wakelock.ts';
import { button, h } from '../ui/dom.ts';

const MIN_SCALE = 0.9;
const MAX_SCALE = 3.5;

export async function renderStage(params: Record<string, string>): Promise<View> {
  const settings = await getSettings();
  const isSetlist = params.index !== undefined;

  let songs: Song[] = [];
  let backHash = '#/';
  let listName = '';

  if (isSetlist) {
    const setlist = await getSetlist(params.id as string);
    if (!setlist) {
      return {
        title: 'Setlist nicht gefunden',
        back: '#/setlists',
        body: h('div', { class: 'empty', text: 'Diese Setlist wurde geloescht.' }),
      };
    }
    listName = setlist.name;
    backHash = `#/setlist/${setlist.id}`;
    const loaded = await Promise.all(setlist.songIds.map((songId) => getSong(songId)));
    songs = loaded.filter((s): s is Song => Boolean(s));
  } else {
    const song = await getSong(params.id as string);
    if (!song) {
      return {
        title: 'Song nicht gefunden',
        back: '#/',
        body: h('div', { class: 'empty', text: 'Dieser Song wurde geloescht.' }),
      };
    }
    songs = [song];
    backHash = `#/sheet/${song.id}`;
  }

  if (songs.length === 0) {
    return {
      title: listName || 'Bühne',
      back: backHash,
      body: h('div', { class: 'empty', text: 'Diese Setlist enthält keine Songs mehr.' }),
    };
  }

  const index = Math.min(Math.max(Number(params.index ?? '0') || 0, 0), songs.length - 1);
  const song = songs[index] as Song;
  let scale = settings.stageFontScale;

  const wakeLock = settings.keepAwake ? keepAwake() : null;

  /** Wechselt den Song ueber den Router. */
  function step(delta: number): void {
    const next = index + delta;
    if (next < 0 || next >= songs.length) return;
    navigate(`#/stage/setlist/${params.id}/${next}`, true);
  }

  const panel = createSheetPanel({
    song,
    scale,
    // Titel steht in der Leiste – das spart Platz auf der Buehne.
    showHead: false,
    fallbackSpeed: settings.defaultScrollSpeed,
    onSpeedChange: (speed) => void saveSong({ ...current, scrollSpeed: speed }).then((s) => (current = s)),
    onTap: () => panel.scroller.toggle(),
    onSwipe: (direction) => step(direction === 'left' ? 1 : -1),
  });

  function changeScale(delta: number): void {
    scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, Math.round((scale + delta) * 20) / 20));
    panel.setScale(scale);
    void saveSettings({ ...settings, stageFontScale: scale });
    requestAnimationFrame(refreshDrawing);
  }

  const meta = [song.key, song.tempo ? `${song.tempo} bpm` : '', song.timeSignature]
    .filter(Boolean)
    .join(' · ');

  const prevBtn = button('‹', () => step(-1), {
    class: 'btn btn--ghost btn--icon',
    title: 'Vorheriger Song',
  });
  prevBtn.disabled = index === 0;
  const nextBtn = button('›', () => step(1), {
    class: 'btn btn--ghost btn--icon',
    title: 'Nächster Song',
  });
  nextBtn.disabled = index === songs.length - 1;

  const bar = h(
    'header',
    { class: 'stage__bar' },
    button('✕', () => navigate(backHash), {
      class: 'btn btn--ghost btn--icon',
      title: 'Bühnen-Modus verlassen',
    }),
    h('span', {
      class: 'stage__pos',
      text: songs.length > 1 ? `${index + 1}/${songs.length}` : meta,
    }),
    h('span', { class: 'stage__title', text: song.title || 'Ohne Titel', attrs: { title: meta } }),
    button('A−', () => changeScale(-0.15), { class: 'btn btn--ghost btn--icon', title: 'Kleiner' }),
    button('A+', () => changeScale(0.15), { class: 'btn btn--ghost btn--icon', title: 'Größer' }),
    prevBtn,
    nextBtn,
  );

  /* Aufnahme zum Mitspielen – auf der Buehne ebenso hinzufuegbar wie beim Ueben. */
  let current = song;
  const audioPanel = createAudioPanel({
    song,
    allowAdd: true,
    onChange: async (audio) => {
      current = await saveSong({ ...current, audio });
    },
  });
  audioPanel.tryAutoplay();

  const container = h('div', { class: 'app stage' }, bar, audioPanel.element, panel.element);

  return {
    title: listName || 'Bühne',
    fullscreen: true,
    body: container,
    drawing: { id: songSurfaceId(song.id), ...panel.drawing },
    cleanup: () => {
      panel.destroy();
      audioPanel.destroy();
      wakeLock?.release();
    },
  };
}
